import { Clickable, H2, H3, H4, PageNavigationBar, Section, Wrapper } from '@ahmdigital/ui';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { filter, flow, get, map, reject } from 'lodash/fp';

import { ContentContainer, SectionContainer, SectionWrapper } from './styles';
import { RootState } from '../../reducers';
import AllProductsDataLoader from '../../components/all-products-data-loader';
import BackToTop from '../../elements/back-to-top';
import Banner from '../../elements/banner';
import constants from '../../ahm-constants';
import data, { SitemapLink, SitemapSection } from './data';
import Head from '../../../utils/head';
import HorizontalRule from '../../elements/horizontal-rule';
import LegacyBorderedWrapper from '../../components/legacy-bordered-wrapper';
import ProductsByType from './components/products-by-type';
import styles from './styles.module.sass';
import textStyles from '../../application/common/text.module.sass';
import uncapFpIterator from '../../../utils/uncap-fp-iterator';

type SitemapPageProps = {
  products: {};
};

const SitemapPage = ({ products }: SitemapPageProps) => {
  const sections: SitemapSection[] = data.getSections();
  const icon = { name: 'chevron-right', position: 'right' } as const;

  const mapSections = flow(
    filter((section: SitemapSection) => !section.isSubSection),
    map((section: SitemapSection) => ({
      id: section.slug,
      text: section.title,
    })),
  );

  const items = mapSections(data.getSections());

  const pageNavBarItems = [{ id: 'health-insurance-products', text: 'Health insurance products' }, ...items];

  return (
    <LegacyBorderedWrapper>
      <Head>
        <title>Sitemap</title>
        <meta name="description" content="Literally that … it’s got everything we have on our site." />
      </Head>
      <Banner title="Sitemap" />
      <ContentContainer id="backToTopPoint" tabIndex={-1}>
        <PageNavigationBar items={pageNavBarItems} variant="white" />
        <SectionContainer variant="paddingBottomSpacer9">
          <SectionWrapper id="health-insurance-products">
            <Section variant="paddingTopSpacer4">
              <Wrapper>
                <div className={textStyles.text}>
                  <H2 variant="marginBottomSpacer9">Health insurance products</H2>
                  <H3 variant="marginBottomSpacer9">
                    <Clickable icon={icon} to={constants.URLS.SALES.REFINE_PRICE}>
                      All health insurance products
                    </Clickable>
                  </H3>
                  <H4 variant={['paddingBottomSpacer9', 'paddingTopSpacer12']}>
                    <Clickable icon={icon} to={constants.URLS.SALES.CHOOSE_YOUR_COVER_COMBINED_CHOOSE}>
                      Choose your own hospital & extras
                    </Clickable>
                  </H4>
                  <H4>Hospital & extras packages</H4>
                  <ProductsByType type={constants.PRODUCT_TYPE.PACKAGE} products={products} />
                  <H4>
                    <Clickable icon={icon} to={constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL}>
                      Hospital cover
                    </Clickable>
                  </H4>
                  <ProductsByType type={constants.PRODUCT_TYPE.HOSPITAL} products={products} />
                  <H4>
                    <Clickable icon={icon} to={constants.URLS.SALES.PRODUCT_LISTING_EXTRAS}>
                      Extras cover
                    </Clickable>
                  </H4>
                  <ProductsByType type={constants.PRODUCT_TYPE.EXTRAS} products={products} />
                  <H3>Health insurance member perks</H3>
                  <span>
                    <ul>
                      {map(
                        ({ label, to }) => (
                          <li key={to}>
                            <Clickable to={to}>{label}</Clickable>
                          </li>
                        ),
                        data.getPerks(),
                      )}
                    </ul>
                  </span>
                </div>
              </Wrapper>
            </Section>
          </SectionWrapper>

          {uncapFpIterator(map)(
            (section: SitemapSection, sectionIndex: number) => (
              <SectionWrapper id={section.slug} key={sectionIndex}>
                <Wrapper>
                  <div className={textStyles.text}>
                    {!section.isSubSection && <HorizontalRule className={styles.hr} />}
                    {section.isSubSection ? <H3>{section.title}</H3> : <H2>{section.title}</H2>}
                    <ul>
                      {flow(
                        get('links'),
                        reject({ enabled: false }),
                        map(({ label, to }: SitemapLink) => (
                          <li key={section.slug + label}>
                            <Clickable to={to}>{label}</Clickable>
                          </li>
                        )),
                      )(section)}
                    </ul>
                  </div>
                </Wrapper>
              </SectionWrapper>
            ),
            sections,
          )}
        </SectionContainer>
        <BackToTop />
      </ContentContainer>
    </LegacyBorderedWrapper>
  );
};

const mapStateToProps = (state: RootState) => ({
  products: state.product.data,
});

export default compose(AllProductsDataLoader, connect(mapStateToProps))(SitemapPage);

export { SitemapPage as SitemapPageForTest, mapStateToProps };
